import React from "react";

import {
  ChronologySortField,
  ChronologyViewType,
  SortDirection,
  type VisitDisplay,
  type VisitsFlatItem,
} from "../types";
import {
  buildGroupedVisitEntries,
  buildVisitsFlatItems,
  getVisibleGroupedEntries,
} from "../utils/buildVisitsChronologyLayout";

export interface UseVisitsChronologyFlatListArgs {
  visits: VisitDisplay[];
  groupTherapyVisits: (list: VisitDisplay[], preserveOrder?: boolean) => VisitDisplay[];
  viewType: ChronologyViewType;
  fileMapping: Record<string, string>;
  chronologySortField: ChronologySortField;
  chronologySortDirection: SortDirection;
  formatVisitType?: (visitTypeId: string) => string;
  onFlatItemsChange?: (getIndexForVisitId: (visitId: string) => number | null) => void;
  preserveOrder?: boolean;
}

export interface UseVisitsChronologyFlatListResult {
  flatItems: VisitsFlatItem[];
  /** Group key → visits in that group (same order as headers in the flat list). */
  visibleGroupedEntries: [string, VisitDisplay[]][];
  expandedGroups: Record<string, boolean>;
  setExpandedGroups: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

export function useVisitsChronologyFlatList({
  visits,
  groupTherapyVisits,
  viewType,
  fileMapping,
  chronologySortField,
  chronologySortDirection,
  formatVisitType,
  onFlatItemsChange,
  preserveOrder = false,
}: UseVisitsChronologyFlatListArgs): UseVisitsChronologyFlatListResult {
  const processedVisits = React.useMemo(
    () => groupTherapyVisits(visits, preserveOrder),
    [groupTherapyVisits, visits, preserveOrder]
  );

  const groupedEntries: [string, VisitDisplay[]][] = React.useMemo(
    () =>
      buildGroupedVisitEntries(
        processedVisits,
        viewType,
        fileMapping,
        chronologySortField,
        chronologySortDirection,
        formatVisitType,
        preserveOrder
      ),
    [
      processedVisits,
      viewType,
      fileMapping,
      chronologySortField,
      chronologySortDirection,
      formatVisitType,
      preserveOrder,
    ]
  );

  const visibleGroupedEntries = React.useMemo(
    () => getVisibleGroupedEntries(groupedEntries, viewType),
    [groupedEntries, viewType]
  );

  const [expandedGroups, setExpandedGroups] = React.useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      visibleGroupedEntries.map(([key]) => [key, viewType === ChronologyViewType.DATE])
    )
  );

  React.useEffect(() => {
    setExpandedGroups(
      Object.fromEntries(
        visibleGroupedEntries.map(([key]) => [key, viewType === ChronologyViewType.DATE])
      )
    );
  }, [viewType]); // Intentionally omit visibleGroupedEntries — do not reset user expansion on data refresh

  const flatItems = React.useMemo<VisitsFlatItem[]>(
    () => buildVisitsFlatItems(visibleGroupedEntries, expandedGroups, viewType, fileMapping),
    [visibleGroupedEntries, expandedGroups, viewType, fileMapping]
  );

  const flatItemsRef = React.useRef(flatItems);
  flatItemsRef.current = flatItems;

  React.useEffect(() => {
    onFlatItemsChange?.((visitId: string) => {
      const idx = flatItemsRef.current.findIndex(
        (item) => item.kind === "visit" && item.visit.visit_id === visitId
      );
      return idx === -1 ? null : idx;
    });
  }, [onFlatItemsChange]);

  return { flatItems, visibleGroupedEntries, expandedGroups, setExpandedGroups };
}
